import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons';

import { Switch } from '@buildoutinc/blueprint-react/ui/Switch';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';
import { Tabs } from '@buildoutinc/blueprint-react/ui/Tabs';

import { Button } from 'components/ui/button';
import { Errors } from 'components/ui/errors';
import { showSnackbar } from 'components/ui/snackbar/helper';
import Section from 'bundles/os/project_show/listings_form/section';
import { put } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';

import { demographicsCategories, demographicsFieldsFor } from './demographics_constants';
import OsRingEditor from './os_ring_editor';
import { UnsavedChangesModal } from './unsaved_data_guard';

interface Props {
  defaultRadiusRings: number[];
  hiddenFields: string[];
  maxRadiusMiles: number;
  radiusRings: (number | null)[];
  savePath: string;
}

interface SaveResponse {
  hiddenFields: string[];
  radii: number[];
}

interface SavedDefaults {
  hiddenFields: string[];
  radiusRings: (number | null)[];
}

const UNSAVED_CHANGES = 'You have unsaved changes. Are you sure you want to leave this page?';

const sameHiddenFields = (a: Set<string>, b: string[]) => (
  JSON.stringify([...a].sort()) === JSON.stringify([...b].sort())
);

export default function DemographicsDefaultsEditor(props: Props) {
  const [radiusRings, setRadiusRings] = useState<(number | null)[]>(props.radiusRings);
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set(props.hiddenFields));
  const [saved, setSaved] = useState<SavedDefaults>({
    hiddenFields: props.hiddenFields,
    radiusRings: props.radiusRings
  });
  const [activeCategory, setActiveCategory] = useState<string>('overview');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [pendingTab, setPendingTab] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const leavingRef = useRef<boolean>(false);

  const isDirty = JSON.stringify(radiusRings) !== JSON.stringify(saved.radiusRings) ||
    !sameHiddenFields(hiddenFields, saved.hiddenFields);
  const isBuildoutDefault = JSON.stringify(radiusRings) === JSON.stringify(props.defaultRadiusRings) &&
    hiddenFields.size === 0;
  const allRadiiValid = radiusRings.length > 0 &&
    radiusRings.every(r => typeof r === 'number' && r > 0 && r <= props.maxRadiusMiles);
  const categoryFields = demographicsFieldsFor(activeCategory);
  const allCategoryRowsVisible = categoryFields.every(field => !hiddenFields.has(field.key));
  const allCategoryRowsHidden = categoryFields.every(field => hiddenFields.has(field.key));

  useEffect(() => {
    if (!isDirty) return undefined;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const onTabClick = (e: MouseEvent) => {
      if (leavingRef.current) return;

      const tab = (e.target as Element | null)?.closest<HTMLElement>('[data-bs-toggle="tab"]');
      const target = tab?.getAttribute('data-bs-target');
      if (!tab || !target || document.querySelector(target)?.contains(containerRef.current)) return;

      e.preventDefault();
      e.stopPropagation();
      setPendingTab(tab);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onTabClick, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onTabClick, { capture: true });
    };
  }, [isDirty]);

  const discardAndLeave = () => {
    const tab = pendingTab;
    setRadiusRings(saved.radiusRings);
    setHiddenFields(new Set(saved.hiddenFields));
    setErrors([]);
    setPendingTab(null);

    leavingRef.current = true;
    tab?.click();
    leavingRef.current = false;
  };

  const setRowsHidden = (keys: string[], hidden: boolean) => {
    setHiddenFields((prev) => {
      const next = new Set(prev);
      keys.forEach(key => (hidden ? next.add(key) : next.delete(key)));
      return next;
    });
  };

  const resetToBuildoutDefaults = () => {
    setRadiusRings(props.defaultRadiusRings);
    setHiddenFields(new Set());
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrors([]);

    try {
      const response: SaveResponse = await put(props.savePath, {
        demographicsDefault: { radii: radiusRings, hiddenFields: [...hiddenFields] },
      });
      setRadiusRings(response.radii);
      setHiddenFields(new Set(response.hiddenFields));
      setSaved({ hiddenFields: response.hiddenFields, radiusRings: response.radii });
      showSnackbar({ text: 'Saved successfully', variant: 'success' });
    } catch (e) {
      setErrors(requestErrors(e, 'Demographics defaults could not be saved'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-4" ref={containerRef}>
      <UnsavedChangesModal
        message={UNSAVED_CHANGES}
        show={pendingTab !== null}
        onLeave={discardAndLeave}
        onStay={() => setPendingTab(null)}
      />

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h5 className="mb-1">Demographics Defaults</h5>
          <p className="text-muted mb-0">
            These defaults apply to new listings only. Existing listings aren&apos;t changed, and brokers can still
            adjust the radius rings and rows on each listing.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            disabled={isSaving || isBuildoutDefault}
            icon={faArrowRotateLeft}
            label="Reset to Buildout Defaults"
            variant="outline"
            onClick={resetToBuildoutDefaults}
          />
          <Button
            data-testid="save-button"
            disabled={isSaving || !isDirty || !allRadiiValid}
            label={isSaving ? 'Saving ...' : 'Save Changes'}
            onClick={handleSave}
          />
        </div>
      </div>

      <Errors errors={errors} />

      <Section title="Default Radius Rings">
        <p className="text-muted fs-small mb-0">New listings start with up to three radius rings.</p>
        <OsRingEditor
          disabled={isSaving}
          radiusRings={radiusRings}
          onAddRadiusRing={() => setRadiusRings(rings => [...rings, null])}
          onRadiusRingChange={(index: number, value: number | null) => (
            setRadiusRings(rings => rings.map((ring, i) => (i === index ? value : ring)))
          )}
          onRemoveRadiusRing={(index: number) => setRadiusRings(rings => rings.filter((_, i) => i !== index))}
        />
        {!allRadiiValid && (
          <p className="text-danger fs-small mb-0">
            Each ring must be greater than 0 and at most {props.maxRadiusMiles} miles.
          </p>
        )}
      </Section>

      <Section title="Default Data Rows">
        <p className="text-muted fs-small mb-0">Rows hidden here are also hidden from exported documents.</p>
        <Tabs
          className="d-flex align-items-start gap-4"
          orientation="vertical"
          value={activeCategory}
          onValueChange={(value: unknown) => setActiveCategory(value as string)}
        >
          <Tabs.List className="flex-shrink-0" orientation="vertical" style={{ width: '160px' }} variant="pills">
            {demographicsCategories.map(category => (
              <Tabs.Tab className="text-start" key={category.field} value={category.field}>{category.label}</Tabs.Tab>
            ))}
          </Tabs.List>
          <Tabs.Panel className="flex-grow-1" style={{ minWidth: 0 }} value={activeCategory}>
            <div className="d-flex justify-content-end gap-2 mb-2">
              <Button
                disabled={isSaving || allCategoryRowsVisible}
                label="Show All"
                size="sm"
                variant="outline"
                onClick={() => setRowsHidden(categoryFields.map(field => field.key), false)}
              />
              <Button
                disabled={isSaving || allCategoryRowsHidden}
                label="Hide All"
                size="sm"
                variant="outline"
                onClick={() => setRowsHidden(categoryFields.map(field => field.key), true)}
              />
            </div>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Metric</Table.Head>
                  <Table.Head className="text-end">Visible</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {categoryFields.map((field) => {
                  const isHidden = hiddenFields.has(field.key);

                  return (
                    <Table.Row key={field.key}>
                      <Table.Cell className={classNames({ 'text-muted': isHidden })}>{field.label}</Table.Cell>
                      <Table.Cell>
                        <div className="d-flex justify-content-end">
                          <Switch
                            aria-label={`${field.label} visible on new listings`}
                            checked={!isHidden}
                            disabled={isSaving}
                            onCheckedChange={(visible: boolean) => setRowsHidden([field.key], !visible)}
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Tabs.Panel>
        </Tabs>
      </Section>
    </div>
  );
}
